import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtVotos } from "@/lib/candidatos";
import { cn } from "@/lib/cn";

type Severidad = "todos" | "normal" | "alto" | "extremo" | "decrecimiento" | "distrito_nuevo";
type Periodo = "2011-2016" | "2016-2026" | "2011-2026";

type DistritoRow = {
  ubigeoReniec: string;
  departamento: string;
  provincia: string;
  distrito: string;
  anioInicio: number;
  anioFin: number;
  electoresInicio: number;
  electoresFin: number;
  delta: number;
  pctCrecimiento: number;
  severidad: string;
};

type Response = {
  meta: {
    periodo: string;
    filtros: { severidad: string; departamento?: string; sort: string; limit: number };
    fuente: string;
    denominadorInei: boolean;
    disclaimer: string;
  };
  resumen: {
    totalDistritos: number;
    porSeveridad: Record<string, number>;
    deptosConExtremo: { departamento: string; extremos: number }[];
  };
  total: number;
  distritos: DistritoRow[];
};

async function fetchData(periodo: Periodo, severidad: Severidad, departamento?: string): Promise<Response> {
  const params: Record<string, string | number> = { periodo, limit: 500 };
  if (severidad !== "todos") params.severidad = severidad;
  if (departamento) params.departamento = departamento;
  const { data } = await api.get<Response>("/laboratorio/crecimiento-padron", { params });
  return data;
}

const chipSev = (s: string) =>
  ({
    extremo: "chip-contradiccion",
    alto: "chip-hipotesis",
    normal: "chip-probado",
    decrecimiento: "chip-vacio",
    distrito_nuevo: "chip-reconocido",
  }[s]) ?? "chip-vacio";

export function CrecimientoPadronPage() {
  const [periodo, setPeriodo] = useState<Periodo>("2011-2026");
  const [severidad, setSeveridad] = useState<Severidad>("extremo");
  const [departamento, setDepartamento] = useState<string>("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["crecimiento", periodo, severidad, departamento],
    queryFn: () => fetchData(periodo, severidad, departamento || undefined),
  });

  return (
    <div className="container-prose py-12 md:py-16">
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip-reconocido">🎯 Laboratorio electoral</span>
        <span className="chip-probado">🟢 Fuente oficial ONPE-INFOGOB + mxm_master</span>
        <span className="chip-vacio">⚫ Sin denominador INEI todavía</span>
      </div>

      <h1 className="mt-6 font-serif text-4xl text-ink-50 md:text-5xl">
        Crecimiento del padrón electoral por distrito
      </h1>
      <p className="mt-3 text-xs uppercase tracking-widest text-gold-400/80">
        2011 → 2016 → 2026 · 1,792 distritos con data completa
      </p>
      <p className="mt-4 max-w-3xl text-lg text-ink-200">
        Crecimiento del padrón electoral distrital entre años consecutivos. Es punto de partida
        para investigación, <strong className="text-ink-50">no conclusión</strong>: un crecimiento
        extremo puede deberse a creación legal de un nuevo distrito, migración por minería, boom
        inmobiliario, frontera VRAEM/selva, o inflado de padrón. Esto último requiere cruzar con
        nacimientos, defunciones y migración de INEI/RENIEC (pendiente).
      </p>

      {/* Disclaimer */}
      <div className="mt-6 rounded-lg border border-sem-vacio/40 bg-ink-900/40 p-4 text-sm text-ink-200">
        <strong className="text-ink-50">⚫ Vacío metodológico declarado:</strong> Sin Censo INEI 2017
        ni estadísticas vitales MINSA/RENIEC, este análisis muestra <em>variación interna del
        padrón electoral</em>. Para distinguir crecimiento legítimo (migración, nuevos electores
        cumpliendo 18 años) de inflado anómalo se necesita el denominador poblacional INEI, fase
        pendiente del proyecto.
      </div>

      {data?.resumen && (
        <section className="mt-8 grid gap-3 md:grid-cols-4">
          <Kpi label="Distritos analizados" value={fmtVotos(data.resumen.totalDistritos)} />
          {(["normal", "alto", "extremo", "distrito_nuevo"] as const).map((k) => (
            <Kpi
              key={k}
              label={
                k === "extremo" ? "EXTREMO (>50%)" :
                k === "alto" ? "Alto (30–50%)" :
                k === "distrito_nuevo" ? "Distrito nuevo por ley" :
                "Normal (<30%)"
              }
              value={String(data.resumen.porSeveridad[k] ?? 0)}
              highlight={k === "extremo"}
            />
          ))}
        </section>
      )}

      {/* Filtros */}
      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-ink-700/40 pt-6">
        <span className="text-sm text-ink-300">Periodo:</span>
        {(["2011-2016", "2016-2026", "2011-2026"] as Periodo[]).map((p) => (
          <button
            key={p}
            onClick={() => setPeriodo(p)}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium uppercase tracking-wide transition",
              periodo === p
                ? "bg-gold-500/15 text-gold-200"
                : "text-ink-300 hover:bg-ink-800 hover:text-ink-50"
            )}
          >
            {p}
          </button>
        ))}
        <span className="ml-4 text-sm text-ink-300">Severidad:</span>
        {(["extremo", "alto", "normal", "decrecimiento", "distrito_nuevo", "todos"] as Severidad[]).map((s) => (
          <button
            key={s}
            onClick={() => setSeveridad(s)}
            className={cn(
              "rounded-md px-3 py-1.5 text-xs font-medium uppercase tracking-wide transition",
              severidad === s
                ? "bg-gold-500/15 text-gold-200"
                : "text-ink-300 hover:bg-ink-800 hover:text-ink-50"
            )}
          >
            {s}
          </button>
        ))}
        <input
          type="text"
          placeholder="Filtrar por departamento…"
          value={departamento}
          onChange={(e) => setDepartamento(e.target.value)}
          className="rounded-md border border-ink-700 bg-ink-950 px-3 py-1.5 text-sm text-ink-100 placeholder:text-ink-400"
        />
      </div>

      {/* Top por departamento */}
      {data?.resumen.deptosConExtremo && data.resumen.deptosConExtremo.length > 0 && severidad === "extremo" && !departamento && (
        <div className="mt-6 rounded-lg border border-ink-700/60 bg-ink-900/30 p-4">
          <div className="text-xs uppercase tracking-widest text-ink-400">Departamentos con más distritos EXTREMO</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {data.resumen.deptosConExtremo.slice(0, 12).map((d) => (
              <button
                key={d.departamento}
                onClick={() => setDepartamento(d.departamento)}
                className="rounded-md border border-ink-700 px-3 py-1 text-xs text-ink-200 hover:border-gold-500/40 hover:text-gold-200"
              >
                {d.departamento} · {d.extremos}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tabla */}
      <div className="mt-4 overflow-x-auto rounded-lg border border-ink-700/60 bg-ink-900/50">
        {isLoading ? (
          <div className="p-6 text-ink-300 text-sm">Cargando…</div>
        ) : error ? (
          <div className="p-6 text-sem-contradiccion text-sm">Error al cargar.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-700/60 text-left text-xs uppercase tracking-wide text-ink-300">
                <th className="py-3 px-3">Distrito</th>
                <th className="px-3">Departamento / Provincia</th>
                <th className="px-3 text-right">Electores {data?.distritos[0]?.anioInicio ?? ""}</th>
                <th className="px-3 text-right">Electores {data?.distritos[0]?.anioFin ?? ""}</th>
                <th className="px-3 text-right">Δ</th>
                <th className="px-3 text-right">% crecimiento</th>
                <th className="px-3">Severidad</th>
              </tr>
            </thead>
            <tbody>
              {data?.distritos.map((d) => (
                <tr key={d.ubigeoReniec + d.anioInicio + d.anioFin} className="border-b border-ink-700/30 hover:bg-ink-800/50">
                  <td className="py-2 px-3">
                    <div className="text-ink-50">{d.distrito}</div>
                    <div className="text-xs text-ink-400 font-mono">RENIEC {d.ubigeoReniec}</div>
                  </td>
                  <td className="px-3 text-ink-300 text-xs">
                    {d.departamento}
                    <br />
                    <span className="text-ink-400">{d.provincia}</span>
                  </td>
                  <td className="px-3 text-right font-mono tabular-nums text-ink-200">{fmtVotos(d.electoresInicio)}</td>
                  <td className="px-3 text-right font-mono tabular-nums text-ink-100">{fmtVotos(d.electoresFin)}</td>
                  <td className="px-3 text-right font-mono tabular-nums">
                    <span className={d.delta >= 0 ? "text-gold-300" : "text-sem-vacio"}>
                      {d.delta >= 0 ? "+" : ""}
                      {fmtVotos(d.delta)}
                    </span>
                  </td>
                  <td className="px-3 text-right font-mono tabular-nums">
                    <span className={d.pctCrecimiento >= 50 ? "text-sem-contradiccion" : d.pctCrecimiento >= 30 ? "text-sem-hipotesis" : "text-ink-200"}>
                      {d.pctCrecimiento >= 0 ? "+" : ""}
                      {d.pctCrecimiento.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-3">
                    <span className={chipSev(d.severidad)}>{d.severidad}</span>
                  </td>
                </tr>
              ))}
              {data && data.distritos.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-ink-400">
                    Sin distritos con los filtros aplicados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Limitaciones explícitas */}
      <div className="mt-6 rounded-lg border border-ink-700/40 bg-ink-900/30 p-4 text-xs text-ink-300 space-y-2">
        <div><strong className="text-ink-100">Fuente:</strong> {data?.meta.fuente}</div>
        <div><strong className="text-ink-100">Distritos sin match nombre-canon:</strong> ~2,400 filas de 2011/2016 no se mapearon (40%) por cambios administrativos entre censos · son TODO pendiente</div>
        <div><strong className="text-ink-100">Lo que NO prueba este análisis:</strong> que el crecimiento extremo sea fraude. Puede ser distrito nuevo, migración, minería, boom inmobiliario. Para inferir anomalía hay que cruzar con población INEI 2017 + nacimientos/defunciones MINSA (pendiente)</div>
      </div>
    </div>
  );
}

function Kpi({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? "border-sem-contradiccion/40 bg-sem-contradiccion/5" : "border-ink-700/60 bg-ink-900/60"}`}>
      <div className="text-xs uppercase tracking-widest text-ink-400">{label}</div>
      <div className={`mt-1 font-mono text-xl tabular-nums ${highlight ? "text-sem-contradiccion" : "text-ink-50"}`}>{value}</div>
    </div>
  );
}
